import React, { useEffect, useState, useRef } from "react";
import "./pageloader.css";

interface PageLoaderProps {
  done: boolean;
  onComplete: () => void;
}

const PageLoader: React.FC<PageLoaderProps> = ({ done, onComplete }) => {
  const [built, setBuilt] = useState(false);
  const [exiting, setExiting] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const t = window.setTimeout(() => setBuilt(true), 2800);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!built || !done) return;
    setExiting(true);
    const t = window.setTimeout(() => onCompleteRef.current(), 500);
    return () => window.clearTimeout(t);
  }, [built, done]);

  return (
    <div className={`page-loader${exiting ? " pl-exiting" : ""}`}>
      <div className="pl-scene">
        <svg className="pl-village" viewBox="0 0 400 260" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">

          {/* Ground */}
          <g className="pl-ground">
            <ellipse cx="200" cy="242" rx="192" ry="14" fill="#1a1208" />
          </g>

          {/* LEFT HUT — pl-hut-1, rust roof */}
          <g className="pl-hut pl-hut-1">
            <g className="pl-hut-body">
              <rect x="59" y="168" width="72" height="62" rx="8" fill="#f0e8d0" stroke="#1a0a02" strokeWidth="2.5" />
              <rect x="59" y="188" width="72" height="19" fill="#e8dfc5" />
              <line x1="59" y1="188" x2="131" y2="188" stroke="#1a0a02" strokeWidth="1.5" />
              <line x1="59" y1="207" x2="131" y2="207" stroke="#1a0a02" strokeWidth="1.5" />
              <polygon points="66,207 71,196 76,207" fill="none" stroke="#1a0a02" strokeWidth="1.3" />
              <circle cx="83" cy="200" r="3" fill="none" stroke="#1a0a02" strokeWidth="1.3" />
              <polygon points="88,207 93,196 98,207" fill="none" stroke="#1a0a02" strokeWidth="1.3" />
              <circle cx="105" cy="200" r="3" fill="none" stroke="#1a0a02" strokeWidth="1.3" />
              <polygon points="111,207 116,196 121,207" fill="none" stroke="#1a0a02" strokeWidth="1.3" />
              <path d="M 87,230 L 87,214 A 8,8 0 0,0 103,214 L 103,230 Z" fill="#1a0a02" />
            </g>
            <g className="pl-hut-roof">
              <path d="M 95,88 L 33,172 Q 95,180 157,172 Z" fill="#c4613a" stroke="#1a0a02" strokeWidth="2.5" />
              <line x1="95" y1="88" x2="43" y2="173" stroke="#1a0a02" strokeWidth="1" />
              <line x1="95" y1="88" x2="54" y2="175" stroke="#1a0a02" strokeWidth="1" />
              <line x1="95" y1="88" x2="65" y2="176" stroke="#1a0a02" strokeWidth="1" />
              <line x1="95" y1="88" x2="77" y2="177" stroke="#1a0a02" strokeWidth="1" />
              <line x1="95" y1="88" x2="90" y2="177" stroke="#1a0a02" strokeWidth="1" />
              <line x1="95" y1="88" x2="102" y2="177" stroke="#1a0a02" strokeWidth="1" />
              <line x1="95" y1="88" x2="115" y2="176" stroke="#1a0a02" strokeWidth="1" />
              <line x1="95" y1="88" x2="126" y2="175" stroke="#1a0a02" strokeWidth="1" />
              <line x1="95" y1="88" x2="137" y2="173" stroke="#1a0a02" strokeWidth="1" />
              <line x1="95" y1="88" x2="90" y2="75" stroke="#1a0a02" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="95" y1="88" x2="95" y2="72" stroke="#1a0a02" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="95" y1="88" x2="100" y2="75" stroke="#1a0a02" strokeWidth="2.5" strokeLinecap="round" />
            </g>
          </g>

          {/* RIGHT HUT — pl-hut-2, brown roof */}
          <g className="pl-hut pl-hut-2">
            <g className="pl-hut-body">
              <rect x="269" y="168" width="72" height="62" rx="8" fill="#f0e8d0" stroke="#1a0a02" strokeWidth="2.5" />
              <rect x="269" y="188" width="72" height="19" fill="#e8dfc5" />
              <line x1="269" y1="188" x2="341" y2="188" stroke="#1a0a02" strokeWidth="1.5" />
              <line x1="269" y1="207" x2="341" y2="207" stroke="#1a0a02" strokeWidth="1.5" />
              <polygon points="278,207 283,196 288,207" fill="none" stroke="#1a0a02" strokeWidth="1.3" />
              <circle cx="295" cy="200" r="3" fill="none" stroke="#1a0a02" strokeWidth="1.3" />
              <polygon points="299,207 304,196 309,207" fill="none" stroke="#1a0a02" strokeWidth="1.3" />
              <circle cx="316" cy="200" r="3" fill="none" stroke="#1a0a02" strokeWidth="1.3" />
              <polygon points="321,207 326,196 331,207" fill="none" stroke="#1a0a02" strokeWidth="1.3" />
              <path d="M 297,230 L 297,214 A 8,8 0 0,0 313,214 L 313,230 Z" fill="#1a0a02" />
            </g>
            <g className="pl-hut-roof">
              <path d="M 305,88 L 243,172 Q 305,180 367,172 Z" fill="#8b4513" stroke="#1a0a02" strokeWidth="2.5" />
              <line x1="305" y1="88" x2="253" y2="173" stroke="#1a0a02" strokeWidth="1" />
              <line x1="305" y1="88" x2="264" y2="175" stroke="#1a0a02" strokeWidth="1" />
              <line x1="305" y1="88" x2="275" y2="176" stroke="#1a0a02" strokeWidth="1" />
              <line x1="305" y1="88" x2="287" y2="177" stroke="#1a0a02" strokeWidth="1" />
              <line x1="305" y1="88" x2="300" y2="177" stroke="#1a0a02" strokeWidth="1" />
              <line x1="305" y1="88" x2="312" y2="177" stroke="#1a0a02" strokeWidth="1" />
              <line x1="305" y1="88" x2="325" y2="176" stroke="#1a0a02" strokeWidth="1" />
              <line x1="305" y1="88" x2="336" y2="175" stroke="#1a0a02" strokeWidth="1" />
              <line x1="305" y1="88" x2="347" y2="173" stroke="#1a0a02" strokeWidth="1" />
              <line x1="305" y1="88" x2="300" y2="75" stroke="#1a0a02" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="305" y1="88" x2="305" y2="72" stroke="#1a0a02" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="305" y1="88" x2="310" y2="75" stroke="#1a0a02" strokeWidth="2.5" strokeLinecap="round" />
            </g>
          </g>

          {/* CENTER HUT — pl-hut-3, gold roof, drawn last (on top) */}
          <g className="pl-hut pl-hut-3">
            <g className="pl-hut-body">
              <rect x="148" y="148" width="104" height="84" rx="10" fill="#f0e8d0" stroke="#1a0a02" strokeWidth="3" />
              <rect x="148" y="178" width="104" height="22" fill="#e8dfc5" />
              <line x1="148" y1="178" x2="252" y2="178" stroke="#1a0a02" strokeWidth="2" />
              <line x1="148" y1="200" x2="252" y2="200" stroke="#1a0a02" strokeWidth="2" />
              <polygon points="156,200 162,180 168,200" fill="none" stroke="#1a0a02" strokeWidth="1.5" />
              <circle cx="174" cy="189" r="3.5" fill="none" stroke="#1a0a02" strokeWidth="1.5" />
              <polygon points="179,200 185,180 191,200" fill="none" stroke="#1a0a02" strokeWidth="1.5" />
              <circle cx="200" cy="189" r="3.5" fill="none" stroke="#1a0a02" strokeWidth="1.5" />
              <polygon points="209,200 215,180 221,200" fill="none" stroke="#1a0a02" strokeWidth="1.5" />
              <circle cx="228" cy="189" r="3.5" fill="none" stroke="#1a0a02" strokeWidth="1.5" />
              <polygon points="232,200 238,180 244,200" fill="none" stroke="#1a0a02" strokeWidth="1.5" />
              <path d="M 188,232 L 188,208 A 12,12 0 0,0 212,208 L 212,232 Z" fill="#1a0a02" />
            </g>
            <g className="pl-hut-roof">
              <path d="M 200,40 L 118,154 Q 200,164 282,154 Z" fill="#d4a843" stroke="#1a0a02" strokeWidth="3" />
              <line x1="200" y1="40" x2="128" y2="155" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="140" y2="157" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="152" y2="158" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="164" y2="160" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="176" y2="161" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="188" y2="162" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="200" y2="162" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="212" y2="162" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="224" y2="161" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="236" y2="160" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="248" y2="158" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="260" y2="157" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="272" y2="155" stroke="#1a0a02" strokeWidth="1" />
              <line x1="200" y1="40" x2="193" y2="24" stroke="#1a0a02" strokeWidth="3" strokeLinecap="round" />
              <line x1="200" y1="40" x2="197" y2="20" stroke="#1a0a02" strokeWidth="3" strokeLinecap="round" />
              <line x1="200" y1="40" x2="203" y2="20" stroke="#1a0a02" strokeWidth="3" strokeLinecap="round" />
              <line x1="200" y1="40" x2="207" y2="24" stroke="#1a0a02" strokeWidth="3" strokeLinecap="round" />
            </g>
          </g>

        </svg>
        <p className="pl-label">Building your tribe…</p>
      </div>
    </div>
  );
};

export default PageLoader;
